import { useState, useEffect } from 'react'
import { Outlet, NavLink, useLocation } from 'react-router-dom'
import {
  LayoutDashboard, Users, Briefcase, GitBranch, Megaphone,
  CalendarDays, FileText, Receipt, UserCircle, CheckSquare,
  Sparkles, Menu, X, Zap, LogOut, Bell, Search,
  ClipboardCheck, BarChart2, Settings
} from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { useActivity } from '../../context/ActivityContext'
import { mockActivity } from '../../data/mockData'
import CommandPalette from '../CommandPalette/CommandPalette'
import DateRangePicker from '../DateRangePicker/DateRangePicker'

const navItems = [
  { path: '/dashboard',  label: 'Dashboard',  icon: LayoutDashboard },
  { path: '/clients',    label: 'Clients',     icon: Users },
  { path: '/projects',   label: 'Projects',    icon: Briefcase },
  { path: '/pipeline',   label: 'Pipeline',    icon: GitBranch },
  { path: '/campaigns',  label: 'Campaigns',   icon: Megaphone },
  { path: '/calendar',   label: 'Calendar',    icon: CalendarDays },
  { path: '/invoices',   label: 'Invoices',    icon: FileText },
  { path: '/expenses',   label: 'Expenses',    icon: Receipt },
  { path: '/team',       label: 'Team',        icon: UserCircle },
  { path: '/tasks',      label: 'Tasks',       icon: CheckSquare },
  { path: '/attendance', label: 'Attendance',  icon: ClipboardCheck },
  { path: '/reports',    label: 'Reports',     icon: BarChart2 },
  { path: '/ai-suite',   label: 'AI Suite',    icon: Sparkles },
  { path: '/settings',   label: 'Settings',    icon: Settings },
]

const ACTIVITY_ICONS: Record<string, { emoji: string; color: string }> = {
  invoice_paid:      { emoji: '💰', color: '#e8a000' },
  project_started:   { emoji: '🚀', color: '#ffc600' },
  new_client:        { emoji: '🤝', color: '#ffc600' },
  project_done:      { emoji: '✅', color: '#32c864' },
  campaign_launched: { emoji: '📣', color: '#ff8c00' },
  overdue_alert:     { emoji: '⚠️', color: '#cc3300' },
}

// ── Notification bell dropdown ─────────────────────────────────
function NotificationBell() {
  const [open, setOpen] = useState(false)
  const { activities, hasNew, markRead } = useActivity()

  // Merge live session activity on top of static seed data
  const staticSeed = mockActivity.slice(0, 6).map(n => ({
    id: n.id,
    emoji: ACTIVITY_ICONS[n.type]?.emoji ?? '•',
    description: n.description,
    detail: n.detail,
    timeAgo: n.timeAgo,
  }))
  const liveFeed = activities.map(a => ({
    id: a.id,
    emoji: a.emoji,
    description: a.description,
    detail: a.detail,
    timeAgo: a.timeAgo,
  }))
  const notifications = [...liveFeed, ...staticSeed].slice(0, 8)

  const handleOpen = () => {
    setOpen(o => !o)
    if (!open) markRead()
  }

  return (
    <div className="relative">
      <button onClick={handleOpen}
        className="relative flex items-center justify-center w-9 h-9 rounded-lg transition-colors"
        style={{ background: open ? 'rgba(255,198,0,0.12)' : 'transparent', color: '#a0a0a0' }}
        onMouseEnter={e => (e.currentTarget.style.color = '#ffc600')}
        onMouseLeave={e => { if (!open) e.currentTarget.style.color = '#a0a0a0' }}>
        <Bell size={18} />
        {hasNew && (
          <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full animate-pulse" style={{ background: '#cc3300' }} />
        )}
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          {/* Dropdown */}
          <div className="absolute right-0 top-11 w-80 rounded-2xl z-50 overflow-hidden"
            style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.25)', boxShadow: '0 16px 48px rgba(0,0,0,0.7)' }}>
            <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'rgba(255,198,0,0.1)' }}>
              <p className="text-sm font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>Activity</p>
              <div className="flex items-center gap-2">
                {activities.length > 0 && (
                  <span className="text-xs px-1.5 py-0.5 rounded font-semibold"
                    style={{ background: 'rgba(255,198,0,0.15)', color: '#ffc600' }}>
                    {activities.length} new
                  </span>
                )}
                <span className="text-xs" style={{ color: '#555' }}>This session</span>
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {notifications.map((n, i) => (
                <div key={n.id} className="flex gap-3 px-4 py-3 border-b transition-colors"
                  style={{ borderColor: 'rgba(255,198,0,0.06)', background: i < liveFeed.length ? 'rgba(255,198,0,0.03)' : 'transparent' }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#1a1a1a')}
                  onMouseLeave={e => (e.currentTarget.style.background = i < liveFeed.length ? 'rgba(255,198,0,0.03)' : 'transparent')}>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-base"
                    style={{ background: 'rgba(255,198,0,0.1)' }}>
                    {n.emoji}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white leading-snug">{n.description}</p>
                    <p className="text-xs mt-0.5 truncate" style={{ color: '#555' }}>{n.detail}</p>
                  </div>
                  <span className="text-xs flex-shrink-0 mt-0.5" style={{ color: i < liveFeed.length ? '#ffc600' : '#555' }}>{n.timeAgo}</span>
                </div>
              ))}
            </div>
            <div className="px-4 py-3 text-center border-t" style={{ borderColor: 'rgba(255,198,0,0.06)' }}>
              <span className="text-xs" style={{ color: '#555' }}>
                {activities.length === 0 ? 'Actions you take will appear here' : `${activities.length} action${activities.length !== 1 ? 's' : ''} this session`}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default function Layout() {
  const [mobileOpen, setMobileOpen]   = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const location = useLocation()
  const { currentUser, logout } = useAuth()

  // Global Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setPaletteOpen(v => !v)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center gap-2 px-5 py-6 border-b" style={{ borderColor: 'rgba(255,198,0,0.12)' }}>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#ffc600' }}>
          <Zap size={16} color="#000" fill="#000" />
        </div>
        <span className="text-white text-lg font-syne font-bold tracking-wide">Clickbait</span>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {navItems.map(({ path, label, icon: Icon }) => {
          const isActive = location.pathname === path
          return (
            <NavLink
              key={path}
              to={path}
              onClick={() => setMobileOpen(false)}
              style={isActive ? {
                borderLeft: '2px solid #ffc600',
                color: '#ffc600',
                background: 'rgba(255,198,0,0.08)',
                borderRadius: '0 8px 8px 0',
              } : {
                borderLeft: '2px solid transparent',
                color: '#a0a0a0',
                borderRadius: '0 8px 8px 0',
              }}
              className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-all duration-150 hover:text-white"
              onMouseEnter={e => {
                if (!isActive) (e.currentTarget as HTMLElement).style.background = 'rgba(255,198,0,0.04)'
              }}
              onMouseLeave={e => {
                if (!isActive) (e.currentTarget as HTMLElement).style.background = 'transparent'
              }}
            >
              <Icon size={17} />
              <span>{label}</span>
              {label === 'AI Suite' && (
                <span className="ml-auto text-xs px-1.5 py-0.5 rounded font-semibold"
                  style={{ background: 'rgba(255,198,0,0.15)', color: '#ffc600', fontSize: '10px' }}>NEW</span>
              )}
            </NavLink>
          )
        })}
      </nav>

      {/* User */}
      <div className="px-4 py-4 border-t" style={{ borderColor: 'rgba(255,198,0,0.12)' }}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
            style={{ background: '#ffc600', color: '#000' }}>
            {currentUser?.avatarInitials ?? 'CB'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{currentUser?.name ?? 'User'}</p>
            <p className="text-xs truncate capitalize" style={{ color: '#a0a0a0' }}>{currentUser?.role ?? ''}</p>
          </div>
        </div>
        <button onClick={logout}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all"
          style={{ color: '#cc3300', border: '1px solid rgba(204,51,0,0.2)' }}
          onMouseEnter={e => (e.currentTarget.style.background = 'rgba(204,51,0,0.08)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </div>
  )

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: '#000000' }}>
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-60 flex-shrink-0"
        style={{ background: '#0f0f0f', borderRight: '1px solid rgba(255,198,0,0.12)' }}>
        <SidebarContent />
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-60 z-50"
            style={{ background: '#0f0f0f', borderRight: '1px solid rgba(255,198,0,0.12)' }}>
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile header */}
        <div className="md:hidden flex items-center justify-between px-4 py-3"
          style={{ background: '#0f0f0f', borderBottom: '1px solid rgba(255,198,0,0.12)' }}>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: '#ffc600' }}>
              <Zap size={13} color="#000" fill="#000" />
            </div>
            <span className="text-white font-syne font-bold">Clickbait</span>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setPaletteOpen(true)}
              className="flex items-center justify-center w-9 h-9 rounded-lg"
              style={{ color: '#a0a0a0' }}>
              <Search size={17} />
            </button>
            <DateRangePicker />
            <NotificationBell />
            <button onClick={() => setMobileOpen(!mobileOpen)} className="text-white p-1">
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Desktop top bar — search + date range + bell */}
        <div className="hidden md:flex items-center justify-between px-6 py-2" style={{ borderBottom: '1px solid rgba(255,198,0,0.06)' }}>
          {/* Search trigger */}
          <button
            onClick={() => setPaletteOpen(true)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all"
            style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.1)', color: '#555' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,198,0,0.25)'; e.currentTarget.style.color = '#a0a0a0' }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,198,0,0.1)'; e.currentTarget.style.color = '#555' }}>
            <Search size={13} />
            <span style={{ fontSize: 12 }}>Search pages…</span>
            <kbd className="ml-2 text-xs px-1.5 py-0.5 rounded" style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', color: '#444', fontSize: 10 }}>⌘K</kbd>
          </button>
          <div className="flex items-center gap-3">
            <DateRangePicker />
            <NotificationBell />
          </div>
        </div>

        <main className="flex-1 overflow-y-auto" style={{ background: '#000000' }}>
          <Outlet />
        </main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  )
}
